import { createFileRoute, Link, Outlet, useNavigate, useParams } from '@tanstack/react-router'
import { useAtom, useAtomValue } from 'jotai'
import { useBotConfig, useDeleteThread } from '~/api'
import { DeleteConfirmationModal } from '~/components/shared/DeleteConfirmationModal'

import { useEffect, useRef } from 'react'
import { MdMenu } from 'react-icons/md'
import { PromptInput } from '~/components/student/chat/PromptInput'
import { ThreadMenu } from '~/components/student/chat/ThreadMenu'
import { StudentSidebar } from '~/components/student/StudentSidebar'
import { deleteThreadIdAtom, isShowMobileMenuAtom, scrollToBottomOfMessageAtom } from '~/state'
import { Tooltip, TooltipContent, TooltipTrigger } from '~ui'

export const Route = createFileRoute('/student/courses/$courseId')({
	component: StudentLayout,
})
function StudentLayout() {
	const botConfig = useBotConfig()

	const { threadId } = useParams({ strict: false })
	const [deleteThreadId, setDeleteThreadId] = useAtom(deleteThreadIdAtom)
	const [, setShowMobileMenu] = useAtom(isShowMobileMenuAtom)

	const { mutateAsync: deleteThread } = useDeleteThread()
	const navigate = useNavigate()

	const scrollContainerRef = useRef<HTMLDivElement>(null)
	const scrollToBottomCount = useAtomValue(scrollToBottomOfMessageAtom)

	useEffect(() => {
		if (scrollToBottomCount > 0) {
			setTimeout(() => {
				scrollContainerRef.current?.scrollTo({ top: scrollContainerRef.current.scrollHeight, behavior: 'smooth' })
			}, 50)
		}
	}, [scrollToBottomCount])

	return (
		<div className="flex h-full flex-col overflow-y-hidden">
			<header className="flex-shrink-0">
				<div className="bg-navy-100 flex shrink-0 items-center justify-between gap-x-[24px] gap-y-[8px] px-[16px] py-[8px] text-white md:pr-[32px] md:pl-[12px] lg:pr-[84px]">
					<Link
						id="aila-home"
						from="/student/courses/$courseId/apps/$appId"
						to="/student/courses/$courseId/apps/$appId"
						className="flex shrink-0 grow items-center gap-x-[12px] md:gap-x-[24px] lg:gap-x-[48px]"
						aria-label="Aila home"
					>
						<img
							alt="University of Melbourne"
							src="/logos/uom_logo.svg"
							className="hidden h-[48px] lg:block"
							aria-hidden
						/>
						<img
							alt="University of Melbourne"
							src="/logos/uom_logo_vertical.svg"
							className="h-[48px] lg:hidden"
							aria-hidden
						/>
						<img
							alt="Aila"
							src="/logos/aila_logo.svg"
							className="mx-auto h-[26px] md:mx-0"
							aria-hidden
						/>
					</Link>

					<div className="hidden items-center md:flex">
						{botConfig != null && (
							<Tooltip>
								<TooltipTrigger asChild>
									<h1
										title=""
										className="bg-light-blue-25 text-navy-100 h-min w-max cursor-pointer overflow-x-hidden rounded-[2px] px-[8px] py-[2px] text-[14px]! font-normal! text-ellipsis whitespace-nowrap md:max-w-[380px]"
										aria-label={`Course: ${botConfig.tutor_bot_course_name}`}
									>
										{botConfig.tutor_bot_course_name}
									</h1>
								</TooltipTrigger>
								<TooltipContent aria-hidden="true">{botConfig.tutor_bot_course_name}</TooltipContent>
							</Tooltip>
						)}
						<a
							href="https://q.surveys.unimelb.edu.au/jfe/form/SV_835DYg5YmbKAfGu"
							target="_blank"
							rel="noreferrer"
							className="hover:border-light-blue-100 hover:text-light-blue-100 active:border-light-blue-25! ml-[24px] hidden h-[32px] items-center border border-white px-[8px] py-[3px] text-[18px] md:flex"
						>
							Feedback
						</a>
					</div>
					<button
						type="button"
						onMouseDown={() => {
							setShowMobileMenu(true)
						}}
						className="flex size-[40px] flex-shrink-0 items-center justify-center md:hidden"
						aria-label="Open navigation sidebar"
					>
						<MdMenu className="size-[24px]" />
					</button>
				</div>
			</header>
			<div className="h-full">
				<div className="flex h-full">
					<StudentSidebar />
					<div className="flex grow flex-col justify-between">
						<div className="relative flex flex-1 flex-col items-center">
							<div
								ref={scrollContainerRef}
								className="h-full w-full overflow-x-hidden overflow-y-auto px-[16px] md:px-[24px]"
							>
								<div className="mx-auto flex w-full max-w-[724px] flex-col pt-[16px] md:py-[24px]">
									<div className="absolute top-[16px] right-[24px] flex flex-col items-end gap-[12px] md:top-[24px] lg:right-[82px]">{threadId && <ThreadMenu threadId={threadId} />}</div>
									<main
										id="main-content"
										aria-label="Chat messages"
									>
										<Outlet />
									</main>
								</div>
							</div>
						</div>

						<section
							aria-labelledby="prompt-input"
							className="max-h-max px-[16px] pt-[12px] md:pt-[24px]"
						>
							<h2
								id="prompt-input"
								className="sr-only"
							>
								Prompt input
							</h2>
							<PromptInput />
						</section>
					</div>

					<DeleteConfirmationModal
						open={deleteThreadId != null}
						itemType="thread"
						onClose={() => {
							setDeleteThreadId(null)
						}}
						onDelete={() => {
							if (deleteThreadId == null) return
							void deleteThread(deleteThreadId)
							if (threadId === deleteThreadId) {
								void navigate({
									from: '/student/courses/$courseId/apps/$appId',
									to: '/student/courses/$courseId/apps/$appId',
								})
							}
							setDeleteThreadId(null)
						}}
					/>
				</div>
			</div>
		</div>
	)
}
